import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchComboProducts } from "../../../actions/productActions";
import "./comboproduct.css";
import RightArrowIcon from "../../Icons/RightArrow";
import ComboProductModifier from "./comboproductmodifier";

const ComboProductList = (props) => {

  const [openModal, setOpenModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const dispatch = useDispatch();
  const productId = props.comboProductsId;
  const comboProducts = useSelector((state) => state.products.combo_products);
  const comboList = comboProducts?.[0]?.combo_products ?? [];
  const loading = useSelector((state) => state.products.loading);
  const [comboZeroPriceModifier, setComboZeroPriceModifier] = useState([]);
  const [comboModifierPrice, setComboModifierPrice] = useState(0);
  const [selectedModifiers, setSelectedModifiers] = useState([]);
  const [confirmedModifiersByProduct, setConfirmedModifiersByProduct] = useState({});
  const confirmedRef = useRef({});
  const visibleComboList = comboList;
  useEffect(() => {
    if (productId) {
      dispatch(fetchComboProducts(productId));
    }
  }, [productId]);

  useEffect(() => {
    if (!comboList || comboList.length === 0) return;

    const modifierItems = comboList.flatMap((item) =>
      (item.combo_product_modifiers_id ?? []).flatMap((modifierSet) =>
        (modifierSet.modifier ?? []).flatMap((group) =>
          (group.modifier_details ?? []).map((md) => ({
            ...md,
            base_product_id: item.id,
            base_product_name: item.name,
            qty: md.qty ?? 1,
          }))
        )
      )
    );

    const zeroPriceModifierList = modifierItems
      .filter((item) => Number(item.sell_price_inc_tax) === 0)
      .map((item) => ({
        ...item,
        checked: true,
        qty: item.qty ?? 1,
        quantity: item.qty ?? 1,
      }));

    setComboZeroPriceModifier([]);
  }, [comboList]);

  const handleSelectModifier = (modifier) => {
    const selectedMergeModifier = {
      ...modifier,
      qty: modifier.qty ?? 1,
      base_product_id: selectedProduct.id,
      base_product_name: selectedProduct.name,
    };

    setSelectedModifiers((prev) => {
      const exists = prev.find((m) => m.id === modifier.id);
      if (exists) {
        return prev.filter((m) => m.id !== modifier.id);
      }
      return [...prev, selectedMergeModifier];
    });
  };

  const handleConfirm = ({ productModifiers: confirmedModifiers, totalPrice }) => {
    if (!selectedProduct) return;

    // Stamp base_product_id on each modifier — productModifiers is already built
    // in comboproductmodifier.js so no ID reconstruction or type conversion needed here.
    const productModifiers = (confirmedModifiers || []).map((md) => ({
      ...md,
      base_product_id: selectedProduct.id,
      base_product_name: selectedProduct.name,
    }));

    // Use ref to always have latest confirmed data (avoids stale closure)
    confirmedRef.current = {
      ...confirmedRef.current,
      [selectedProduct.id]: productModifiers,
    };
    setConfirmedModifiersByProduct({ ...confirmedRef.current });

    setComboModifierPrice(totalPrice || 0);
    if (props.onModifierPrice) {
      props.onModifierPrice(totalPrice || 0);
    }

    // Pass only current product's data; chooseModal merges by product ID
    props.onZeroModifierData({ productId: selectedProduct.id, modifiers: productModifiers });

    setOpenModal(false);
  };
  if (loading) return <p>Loading...</p>;

  return (
    <div>
      <h3>Choose From Below:</h3>

      {visibleComboList?.length === 0 && <p>No combo products found.</p>}

      <div className="modifier-options-list">
        {visibleComboList?.map((item) => {
          const isDisabled =
            Number(item.out_of_stock) === 1 ||
            Number(item.not_for_selling) === 1 ||
            Number(item.not_for_pickup_delivery) === 1;

          return (
            <div key={item.id} className="modifier-option-item">
              <div className="modifier-option-image-container">
                <img
                  className={`combo-image${Number(item.out_of_stock) === 1 ? " combo-image--oos" : ""}`}
                  src={item.image || '/images/default_placeholder.png'}
                  onError={(e) => { e.target.onerror = null; e.target.src = '/images/default_placeholder.png'; }}
                  alt="combo product"
                />
                {Number(item.out_of_stock) === 1 && (
                  <div className="combo-stock-badge combo-stock-badge--oos">OUT OF STOCK</div>
                )}
                {(Number(item.not_for_selling) === 1 || Number(item.not_for_pickup_delivery) === 1) && Number(item.out_of_stock) !== 1 && (
                  <div className="combo-stock-badge combo-stock-badge--nfs">Not for selling</div>
                )}
              </div>
              <div className="combo-container-footer">
                <div className="top-container">
                  <span align="center" className="boldText">
                    {item.name}
                  </span>
                  <RightArrowIcon
                    groupClass={`arrow-group${isDisabled ? " arrow-group--disabled" : ""}`}
                    onClick={isDisabled ? undefined : () => {
                      setSelectedProduct(item);
                      setOpenModal(true);
                    }}
                  />
                </div>
                <div
                  className={`customize-button${isDisabled ? " customize-button--disabled" : ""}`}
                  onClick={isDisabled ? undefined : () => {
                    setSelectedProduct(item);
                    setOpenModal(true);
                  }}
                >
                  {confirmedModifiersByProduct[item.id]?.length > 0 ? (
                    <span>Customize ({confirmedModifiersByProduct[item.id].reduce((sum, m) => sum + (m.qty || 1), 0)})</span>
                  ) : (
                    "Customize"
                  )}
                </div>
                {props.errorComboIds?.some(id => Number(id) === Number(item.id)) && (
                  <div className="combo-error-text">
                    Required customization missing
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <ComboProductModifier
        open={openModal}
        onClose={() => setOpenModal(false)}
        product={selectedProduct}
        modifierGroups={selectedProduct?.combo_product_modifiers_id || []}
        selectedModifiers={selectedModifiers}
        onSelectModifier={handleSelectModifier}
        onConfirm={handleConfirm}
        previousSelections={confirmedModifiersByProduct[selectedProduct?.id] || []}
      />
    </div>
  );
};

export default ComboProductList;
